import { truncateText } from '@chronosphereio/core';
import { merge } from 'lodash';
import { StateCreator } from 'zustand';
import { PanelDefinition, UnknownSpec } from '@/core';
import { EMPTY_PANEL_GROUP_TITLE } from '@/dashboards/constants';
import { findNextAvailablePosition, getUniquePanelKey } from '../../utils/panelUtils';
import { generateId, Middleware } from './common';
import { DashboardStoreState } from './DashboardProvider';
import { HistorySlice } from './history-slice';
import { addPanelGroup, createEmptyPanelGroup, PanelGroupItemLayout, PanelGroupSlice } from './panel-group-slice';
import { PanelSlice } from './panel-slice';

export interface QuickCreatePanelConfig {
  name: string;
  kind: string;
  spec?: UnknownSpec;
  description?: string;
  query?: string;
  queryType?: 'metrics' | 'logs';
}

export interface QuickActionsSlice {
  /**
   * Quick panel creation without opening the panel editor.
   * Supports both metrics (PrometheusTimeSeriesQuery) and logs (LogsDataQuery) queries.
   * Defaults to metrics query if queryType is not specified.
   */
  quickCreatePanel: (config: QuickCreatePanelConfig) => {
    panelKey: string;
    panelGroupId: number;
  };
  /**
   * Quickly edit an existing panel by key.
   * Supports both metrics (PrometheusTimeSeriesQuery) and logs (LogsDataQuery) queries.
   * Sets edit mode to true so user can save or discard changes.
   */
  quickEditPanel: (config: QuickEditPanelConfig) => {
    panelKey: string;
  };
  /**
   * Apply bulk variable injection updates to multiple panels.
   * Used by useBulkInjectVariable hook after computing panel updates.
   */
  applyBulkVariableInjection: (
    panelUpdates: Record<string, PanelDefinition>,
    metadata: { variableName: string; panelCount: number; queriesModified: number }
  ) => void;
}

export interface QuickEditPanelConfig {
  panelKey: string;
  /** New panel display name */
  name?: string;
  /** New panel description */
  description?: string;
  /** New plugin kind (e.g., TimeSeriesChart) */
  pluginKind?: string;
  /** Partial plugin spec to merge into existing spec */
  pluginSpec?: UnknownSpec;
  /** New query to replace existing */
  query?: string;
  /** Type of query: 'metrics' for PrometheusTimeSeriesQuery, 'logs' for LogsDataQuery */
  queryType?: 'metrics' | 'logs';
}

export function createQuickActionsSlice(): StateCreator<
  QuickActionsSlice & PanelSlice & PanelGroupSlice & DashboardStoreState & HistorySlice,
  Middleware,
  [],
  QuickActionsSlice
> {
  return (set, get) => ({
    quickCreatePanel: (config) => {
      const { name = 'New Panel', query, description, kind, spec, queryType = 'metrics' } = config;

      get().initializeHistoryIfEmpty();

      let panelKey: string | undefined;
      let targetGroupId: number | undefined;

      set(
        (state) => {
          const { panels, panelGroupOrder } = state;
          const panelDefinition: PanelDefinition = {
            kind: 'Panel',
            spec: {
              display: {
                name,
                description,
              },
              plugin: {
                kind,
                spec: spec ?? {},
              },
              queries: query
                ? [
                    {
                      kind: 'DataQuery',
                      spec: {
                        plugin: {
                          kind: queryType === 'logs' ? 'LogsDataQuery' : 'PrometheusTimeSeriesQuery',
                          spec: {
                            query,
                          },
                        },
                      },
                    },
                  ]
                : [],
            },
          };
          panelKey = getUniquePanelKey({ panels });

          // Find or create a panel group if none exist
          targetGroupId = panelGroupOrder[0];
          if (targetGroupId === undefined) {
            const newGroup = createEmptyPanelGroup();
            newGroup.title = EMPTY_PANEL_GROUP_TITLE;
            addPanelGroup(state, newGroup);
            targetGroupId = newGroup.id;
          } else {
            // Check if the first panel group is a Grid group, if not create a new one
            const firstGroup = state.panelGroups[targetGroupId];
            if (firstGroup?.kind !== 'Grid') {
              const newGroup = createEmptyPanelGroup();
              newGroup.title = EMPTY_PANEL_GROUP_TITLE;
              addPanelGroup(state, newGroup);
              targetGroupId = newGroup.id;
            }
          }

          const targetGroup = state.panelGroups[targetGroupId];
          if (!targetGroup || targetGroup.kind !== 'Grid') {
            throw new Error('No grid panel group available to add panel to.');
          }

          // Set layout of the new panel
          const { x, y } = findNextAvailablePosition(targetGroup.itemLayouts, 12, 6);
          const newLayoutItem: PanelGroupItemLayout = {
            i: generateId().toString(),
            x,
            y,
            w: 12,
            h: 6,
          };

          // Add the panel to the dashboard
          state.panels[panelKey] = panelDefinition;
          targetGroup.itemLayouts.push(newLayoutItem);
          targetGroup.itemPanelKeys[newLayoutItem.i] = panelKey;
          state.isEditMode = true;
        },
        false,
        '[Panel] createPanel'
      );

      if (!panelKey || targetGroupId === undefined) {
        throw new Error('Missing panelKey or targetGroupId');
      }

      // Push snapshot AFTER changes so currentIndex points at the mutated state
      get().pushToHistory({
        actionId: `create-panel-${panelKey}`,
        actionName: `Created panel: ${truncateText(name, 20)}`,
      });

      return { panelKey, panelGroupId: targetGroupId };
    },
    quickEditPanel: (config) => {
      const { panelKey, name, description, pluginKind, pluginSpec, query, queryType = 'metrics' } = config;

      get().initializeHistoryIfEmpty();

      set(
        (state) => {
          const panel = state.panels[panelKey];
          if (!panel) {
            throw new Error(`Cannot find panel with key '${panelKey}'`);
          }

          // Update display properties if provided
          if (name !== undefined || description !== undefined) {
            if (!panel.spec.display) {
              panel.spec.display = {};
            }
            if (name !== undefined) {
              panel.spec.display.name = name;
            }
            if (description !== undefined) {
              panel.spec.display.description = description;
            }
          }
          // Update plugin spec (merge into existing spec)
          if (pluginKind !== undefined || pluginSpec !== undefined) {
            if (!panel.spec.plugin) {
              panel.spec.plugin = { kind: '', spec: {} };
            }
            if (pluginKind !== undefined) {
              panel.spec.plugin.kind = pluginKind;
            }
            if (pluginSpec !== undefined) {
              if (!panel.spec.plugin.spec) {
                panel.spec.plugin.spec = {};
              }
              merge(panel.spec.plugin.spec, pluginSpec);
            }
          }
          // Update query if provided (keep existing spec options for series naming)
          if (query) {
            const targetKind = queryType === 'logs' ? 'LogsDataQuery' : 'PrometheusTimeSeriesQuery';
            const firstQuery = panel.spec.queries?.[0];
            if (firstQuery?.spec?.plugin?.kind === targetKind && firstQuery.spec.plugin.spec) {
              // Same query type with existing spec, update the query string
              firstQuery.spec.plugin.spec.query = query;
            } else if (firstQuery) {
              // Different query type or missing spec, replace the query spec
              firstQuery.spec = { plugin: { kind: targetKind, spec: { query } } };
            } else {
              // Create new query when none exists
              panel.spec.queries = [{ kind: 'DataQuery', spec: { plugin: { kind: targetKind, spec: { query } } } }];
            }
          }
          state.isEditMode = true;
        },
        false,
        '[Panel] quickEditPanel'
      );

      // Push snapshot AFTER changes so currentIndex points at the mutated state
      {
        const panel = get().panels[panelKey];
        const displayName = panel?.spec.display?.name || panelKey;
        get().pushToHistory({
          actionId: `edit-panel-${panelKey}`,
          actionName: `Edited panel: ${truncateText(displayName, 20)}`,
        });
      }

      return { panelKey };
    },
    applyBulkVariableInjection: (panelUpdates, metadata) => {
      const { variableName, panelCount } = metadata;

      get().initializeHistoryIfEmpty();

      set(
        (state) => {
          Object.entries(panelUpdates).forEach(([key, panel]) => {
            state.panels[key] = panel;
          });
          state.isEditMode = true;
        },
        false,
        '[Panel] applyBulkVariableInjection'
      );

      // Undo all panel query modifications in a single action
      get().pushToHistory({
        actionId: `bulk-inject-${variableName}-${Date.now()}`,
        actionName: `Injected $${variableName} into ${panelCount} panel${panelCount !== 1 ? 's' : ''}`,
      });
    },
  });
}
